<script setup lang="ts">
import { Loader2, Trash2 } from 'lucide-vue-next'

import type { DnsDiagnostic, EmailDomain } from '@/composables/domainConfig'
import type { ChipColor } from '@/components/base/BaseChip.vue'

const props = defineProps<{
  domain: EmailDomain
  isLoading?: boolean
  diagnostic?: DnsDiagnostic | null
}>()

defineEmits<{
  (e: 'verify', domain: EmailDomain): void
  (e: 'view-dns', domain: EmailDomain): void
  (e: 'test-email', domain: EmailDomain): void
  (e: 'delete', domain: EmailDomain): void
}>()

const { t } = useI18n()

const statusLabel = computed(() => {
  switch (props.domain.status) {
    case 'verified':
      return t('pages.domain.domainCard.status.verified')
    case 'failed':
      return t('pages.domain.domainCard.status.failed')
    default:
      return t('pages.domain.domainCard.status.pending')
  }
})

const statusMessage = computed(() => {
  if (props.domain.is_ready_for_sending) return t('pages.domain.domainCard.readyMessage')

  if (props.diagnostic) {
    return t(`pages.domain.config.steps.dns.diagnostic.summary.${props.diagnostic.summary}.title`, {
      provider: props.diagnostic.provider?.name ?? t('pages.domain.config.steps.dns.diagnostic.yourDnsHost'),
      domain: props.domain.domain
    })
  }

  return t('pages.domain.domainCard.pendingMessage')
})

const statusMessageClass = computed(() => {
  if (props.domain.is_ready_for_sending || !props.diagnostic) return 'text-muted-foreground'

  switch (props.diagnostic.summary) {
    case 'nothing_created':
    case 'incomplete':
    case 'mismatch':
    case 'domain_not_found':
      return 'text-red-600 font-medium'
    case 'propagating':
    case 'ready':
      return 'text-blue-600'
    default:
      return 'text-muted-foreground'
  }
})

const statusColor = computed<ChipColor>(() => {
  switch (props.domain.status) {
    case 'verified':
      return 'green'
    case 'failed':
      return 'red'
    default:
      return 'orange'
  }
})
</script>

<template>
  <Card class="!shadow-none border border-border">
    <CardContent>
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div class="flex items-center gap-4">
          <div class="flex items-center justify-center rounded-md w-10 h-10 aspect-square bg-primary/5">
            <BaseIcon name="IconoirSendMail" class="w-5 h-5 text-primary" />
          </div>
          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-2 flex-wrap">
              <p class="font-medium text-foreground">{{ domain.domain }}</p>
              <BaseChip :color="statusColor" :text="statusLabel" :loading="isLoading" />
              <BaseChip
                :color="domain.is_ready_for_sending ? 'green' : 'orange'"
                :text="
                  domain.is_ready_for_sending
                    ? t('pages.domain.domainCard.status.webhookReady')
                    : t('pages.domain.domainCard.status.webhookPending')
                "
                :loading="isLoading"
              />
            </div>
            <p class="text-xs" :class="statusMessageClass">{{ statusMessage }}</p>
          </div>
        </div>

        <div class="flex items-center gap-2 flex-wrap">
          <Button
            v-if="!domain.is_ready_for_sending"
            variant="outline"
            :disabled="isLoading"
            class="bg-white border !border-border text-foreground"
            @click="$emit('verify', domain)"
          >
            <Loader2 v-if="isLoading" class="h-4 w-4 animate-spin" />
            {{ t('pages.domain.domainCard.verifyButton') }}
          </Button>
          <Button
            v-if="!domain.is_ready_for_sending"
            variant="outline"
            class="bg-white border !border-border text-foreground"
            @click="$emit('view-dns', domain)"
          >
            {{
              diagnostic && diagnostic.summary !== 'propagating' && diagnostic.summary !== 'ready'
                ? t('pages.domain.domainCard.todoButton')
                : t('pages.domain.domainCard.dnsButton')
            }}
          </Button>
          <Button
            v-if="domain.is_ready_for_sending"
            variant="outline"
            class="bg-white border !border-border text-foreground"
            @click.stop="$emit('test-email', domain)"
          >
            {{ t('pages.domain.testEmail.button') }}
          </Button>
          <Button
            variant="outline"
            :aria-label="t('pages.domain.domainCard.deleteButton')"
            :title="t('pages.domain.domainCard.deleteButton')"
            class="bg-white border !border-border text-red-600 hover:text-red-700"
            @click="$emit('delete', domain)"
          >
            <Trash2 class="h-4 w-4" />
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
</template>
